import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import { Keyboard } from '@mui/icons-material';

export interface ShortcutDefinition {
  key: string;
  displayKey: string;
  description: string;
  category: 'View & Split' | 'Auto-Scroll' | 'Navigation' | 'General';
}

export const SHORTCUTS_REGISTRY: ShortcutDefinition[] = [
  { key: 'n', displayKey: 'n', description: 'Increase font size', category: 'View & Split' },
  { key: 'm', displayKey: 'm', description: 'Decrease font size', category: 'View & Split' },
  {
    key: '1',
    displayKey: '1',
    description: 'Switch to 1-column single page view',
    category: 'View & Split',
  },
  {
    key: '2',
    displayKey: '2',
    description: 'Switch to 2-column split page view',
    category: 'View & Split',
  },
  {
    key: '3',
    displayKey: '3',
    description: 'Switch to 3-column split page view',
    category: 'View & Split',
  },
  {
    key: 'f',
    displayKey: 'f',
    description: 'Toggle fullscreen mode',
    category: 'View & Split',
  },
  {
    key: 'w',
    displayKey: 'w',
    description: 'Toggle auto-scroll (and open scroll controls)',
    category: 'Auto-Scroll',
  },
  {
    key: 'q',
    displayKey: 'q',
    description: 'Decrease auto-scroll speed',
    category: 'Auto-Scroll',
  },
  {
    key: 'e',
    displayKey: 'e',
    description: 'Increase auto-scroll speed',
    category: 'Auto-Scroll',
  },
  {
    key: '.',
    displayKey: '.',
    description: 'Turn to next page / section',
    category: 'Navigation',
  },
  {
    key: ',',
    displayKey: ',',
    description: 'Turn to previous page / section',
    category: 'Navigation',
  },
  {
    key: '?',
    displayKey: '?',
    description: 'Open keyboard shortcuts cheatsheet',
    category: 'General',
  },
];

interface ShortcutsModalProps {
  open: boolean;
  onClose: () => void;
}

export const ShortcutsModal: React.FC<ShortcutsModalProps> = ({ open, onClose }) => {
  const categories: ShortcutDefinition['category'][] = [
    'View & Split',
    'Auto-Scroll',
    'Navigation',
    'General',
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          fontWeight: 'bold',
          borderBottom: '1px solid #e2e8f0',
          py: 2,
        }}
      >
        <Keyboard color="primary" />
        <Typography variant="h6" component="span" fontWeight="bold">
          Keyboard Shortcuts
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5 }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Quickly navigate, scroll, and customize song viewing using these hotkeys.
        </Typography>

        {categories.map((category) => {
          const items = SHORTCUTS_REGISTRY.filter((s) => s.category === category);
          if (items.length === 0) return null;

          return (
            <Box key={category} mb={2.5}>
              <Typography
                variant="caption"
                fontWeight="bold"
                color="primary"
                textTransform="uppercase"
                letterSpacing={0.5}
                display="block"
                mb={1}
              >
                {category}
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 2, borderColor: '#e2e8f0', bgcolor: '#f8fafc' }}
              >
                <Table size="small" aria-label={`${category} shortcuts`}>
                  <TableBody>
                    {items.map((shortcut) => (
                      <TableRow
                        key={shortcut.key}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:hover': { bgcolor: '#f1f5f9' },
                        }}
                      >
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{
                            width: 90,
                            py: 1,
                            px: 1.5,
                            borderBottom: '1px solid #e2e8f0',
                          }}
                        >
                          <Chip
                            label={shortcut.displayKey}
                            size="small"
                            sx={{
                              fontFamily: 'monospace',
                              fontWeight: 'bold',
                              fontSize: '0.85rem',
                              bgcolor: '#ffffff',
                              border: '1px solid #cbd5e1',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                              minWidth: 32,
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 1,
                            px: 1.5,
                            borderBottom: '1px solid #e2e8f0',
                            color: '#334155',
                            fontSize: '0.9rem',
                          }}
                        >
                          {shortcut.description}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} variant="contained" color="primary" sx={{ textTransform: 'none', borderRadius: 2 }}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};
